import React, { useMemo } from "react";
import * as THREE from "three";

function mulberry32(seed) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// --- City-style ground texture painters --------------------------------
// Each theme paints the entire 1024×1024 canvas (no tiling, so individual
// features like crosswalks and parking lots stay where they belong).

function paintGrassBase(ctx, px, grassColor) {
  ctx.fillStyle = grassColor;
  ctx.fillRect(0, 0, px, px);
  // Two-tone grass blotches for texture
  for (let i = 0; i < 800; i++) {
    const x = Math.random() * px;
    const y = Math.random() * px;
    const r = 3 + Math.random() * 14;
    ctx.fillStyle =
      Math.random() < 0.55
        ? `rgba(255,255,255,${0.025 + Math.random() * 0.05})`
        : `rgba(0,0,0,${0.05 + Math.random() * 0.1})`;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

function paintSidewalkStrip(ctx, x, y, w, h) {
  ctx.fillStyle = "#444452";
  ctx.fillRect(x, y, w, h);
  // panel seams
  ctx.strokeStyle = "rgba(0,0,0,0.35)";
  ctx.lineWidth = 1;
  const step = 28;
  ctx.beginPath();
  if (w > h) {
    for (let i = 0; i < w; i += step) {
      ctx.moveTo(x + i, y);
      ctx.lineTo(x + i, y + h);
    }
  } else {
    for (let i = 0; i < h; i += step) {
      ctx.moveTo(x, y + i);
      ctx.lineTo(x + w, y + i);
    }
  }
  ctx.stroke();
}

function paintRoad(ctx, x, y, w, h, horizontal) {
  // Asphalt
  ctx.fillStyle = "#181820";
  ctx.fillRect(x, y, w, h);
  // Tar speckle
  for (let i = 0; i < 220; i++) {
    const rx = x + Math.random() * w;
    const ry = y + Math.random() * h;
    ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.04})`;
    ctx.fillRect(rx, ry, 2, 2);
  }
  // White edge lines
  ctx.strokeStyle = "rgba(225,225,225,0.6)";
  ctx.lineWidth = 3;
  ctx.beginPath();
  if (horizontal) {
    ctx.moveTo(x + 4, y + 6);
    ctx.lineTo(x + w - 4, y + 6);
    ctx.moveTo(x + 4, y + h - 6);
    ctx.lineTo(x + w - 4, y + h - 6);
  } else {
    ctx.moveTo(x + 6, y + 4);
    ctx.lineTo(x + 6, y + h - 4);
    ctx.moveTo(x + w - 6, y + 4);
    ctx.lineTo(x + w - 6, y + h - 4);
  }
  ctx.stroke();
  // Yellow dashed center lane
  ctx.strokeStyle = "#f3c93b";
  ctx.lineWidth = 3;
  ctx.setLineDash([28, 22]);
  ctx.beginPath();
  if (horizontal) {
    ctx.moveTo(x, y + h / 2);
    ctx.lineTo(x + w, y + h / 2);
  } else {
    ctx.moveTo(x + w / 2, y);
    ctx.lineTo(x + w / 2, y + h);
  }
  ctx.stroke();
  ctx.setLineDash([]);
}

function paintCrosswalk(ctx, cx, cy, alongLen, acrossLen, horizontal) {
  ctx.fillStyle = "rgba(235,235,235,0.85)";
  const stripeCount = 6;
  const gap = 4;
  const stripe = (acrossLen - gap * (stripeCount + 1)) / stripeCount;
  for (let i = 0; i < stripeCount; i++) {
    if (horizontal) {
      const y = cy - acrossLen / 2 + gap + i * (stripe + gap);
      ctx.fillRect(cx - alongLen / 2, y, alongLen, stripe);
    } else {
      const x = cx - acrossLen / 2 + gap + i * (stripe + gap);
      ctx.fillRect(x, cy - alongLen / 2, stripe, alongLen);
    }
  }
}

function paintParkingLot(ctx, x, y, w, h) {
  ctx.fillStyle = "#22222c";
  ctx.fillRect(x, y, w, h);
  // Speckle
  for (let i = 0; i < 120; i++) {
    const rx = x + Math.random() * w;
    const ry = y + Math.random() * h;
    ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.05})`;
    ctx.fillRect(rx, ry, 2, 2);
  }
  // Parking stripes (white) — slots arranged perpendicular to longer side
  ctx.strokeStyle = "rgba(235,235,235,0.75)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  if (w >= h) {
    const slots = Math.max(4, Math.floor(w / 36));
    const slotW = w / slots;
    for (let i = 1; i < slots; i++) {
      const sx = x + i * slotW;
      ctx.moveTo(sx, y + 6);
      ctx.lineTo(sx, y + h - 6);
    }
    ctx.moveTo(x + 4, y + h / 2);
    ctx.lineTo(x + w - 4, y + h / 2);
  } else {
    const slots = Math.max(4, Math.floor(h / 36));
    const slotH = h / slots;
    for (let i = 1; i < slots; i++) {
      const sy = y + i * slotH;
      ctx.moveTo(x + 6, sy);
      ctx.lineTo(x + w - 6, sy);
    }
    ctx.moveTo(x + w / 2, y + 4);
    ctx.lineTo(x + w / 2, y + h - 4);
  }
  ctx.stroke();
}

function paintStone(ctx, x, y, w, h) {
  ctx.fillStyle = "#8a7a6c";
  ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = "rgba(0,0,0,0.35)";
  ctx.lineWidth = 1;
  const step = 22;
  ctx.beginPath();
  if (w >= h) {
    for (let i = step; i < w; i += step) {
      ctx.moveTo(x + i, y);
      ctx.lineTo(x + i, y + h);
    }
  } else {
    for (let i = step; i < h; i += step) {
      ctx.moveTo(x, y + i);
      ctx.lineTo(x + w, y + i);
    }
  }
  ctx.stroke();
}

const THEME_PAINTERS = {
  // Small backyard — grass with a stone path leading to the centre.
  backyard(ctx, px) {
    paintGrassBase(ctx, px, "#22421f");
    // garden patches (lighter green clusters)
    for (let i = 0; i < 6; i++) {
      const cx = 100 + Math.random() * (px - 200);
      const cy = 100 + Math.random() * (px - 200);
      ctx.fillStyle = "rgba(120,180,80,0.18)";
      ctx.beginPath();
      ctx.arc(cx, cy, 50 + Math.random() * 40, 0, Math.PI * 2);
      ctx.fill();
    }
    // stone path crossing the centre
    paintStone(ctx, px * 0.38, 0, px * 0.06, px);
    paintStone(ctx, 0, px * 0.55, px, px * 0.05);
    // Soil patch near edges
    ctx.fillStyle = "rgba(80,55,30,0.5)";
    ctx.fillRect(px * 0.05, px * 0.05, px * 0.18, px * 0.05);
    ctx.fillRect(px * 0.77, px * 0.78, px * 0.18, px * 0.05);
  },

  // Neighbourhood street — cross of asphalt roads, sidewalks, crosswalks,
  // grass lawn fills and two small parking lots.
  street(ctx, px) {
    paintGrassBase(ctx, px, "#284827");
    const roadW = px * 0.14;
    const sw = px * 0.024;
    const hY = px * 0.5 - roadW / 2;
    const vX = px * 0.5 - roadW / 2;
    // Sidewalks
    paintSidewalkStrip(ctx, 0, hY - sw, px, sw);
    paintSidewalkStrip(ctx, 0, hY + roadW, px, sw);
    paintSidewalkStrip(ctx, vX - sw, 0, sw, px);
    paintSidewalkStrip(ctx, vX + roadW, 0, sw, px);
    // Roads
    paintRoad(ctx, 0, hY, px, roadW, true);
    paintRoad(ctx, vX, 0, roadW, px, false);
    // Crosswalks at intersection arms
    const cx = px * 0.5;
    const cy = px * 0.5;
    paintCrosswalk(ctx, cx - roadW / 2 - sw / 2, cy, roadW, sw - 4, false);
    paintCrosswalk(ctx, cx + roadW / 2 + sw / 2, cy, roadW, sw - 4, false);
    paintCrosswalk(ctx, cx, cy - roadW / 2 - sw / 2, roadW, sw - 4, true);
    paintCrosswalk(ctx, cx, cy + roadW / 2 + sw / 2, roadW, sw - 4, true);
    // Driveway-style small parking lots in two corners
    paintParkingLot(ctx, px * 0.78, px * 0.10, px * 0.18, px * 0.10);
    paintParkingLot(ctx, px * 0.06, px * 0.78, px * 0.16, px * 0.12);
    // Driveway connectors
    ctx.fillStyle = "#22222c";
    ctx.fillRect(px * 0.86, px * 0.20, px * 0.04, hY - px * 0.20);
    ctx.fillRect(px * 0.12, px * 0.74, px * 0.04, px * 0.04);
  },

  // Farm — dirt rows, crop stripes
  farm(ctx, px) {
    paintGrassBase(ctx, px, "#3a2a18");
    // Crop rows (darker stripes)
    ctx.fillStyle = "rgba(90,75,45,0.55)";
    const rowH = 32;
    for (let y = 30; y < px - 30; y += rowH) {
      ctx.fillRect(60, y, px - 120, 10);
    }
    // Dirt path crossing
    paintStone(ctx, 0, px * 0.52, px, px * 0.05);
    // Patch of crops with a different hue
    ctx.fillStyle = "rgba(80,140,40,0.18)";
    ctx.fillRect(px * 0.55, px * 0.08, px * 0.35, px * 0.32);
  },

  // Junkyard — cracked concrete
  junkyard(ctx, px) {
    ctx.fillStyle = "#2a2622";
    ctx.fillRect(0, 0, px, px);
    // Oil stains
    for (let i = 0; i < 12; i++) {
      const cx = Math.random() * px;
      const cy = Math.random() * px;
      const r = 18 + Math.random() * 40;
      ctx.fillStyle = "rgba(0,0,0,0.45)";
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
    }
    // Cracks
    ctx.strokeStyle = "rgba(255,255,255,0.05)";
    ctx.lineWidth = 1;
    for (let i = 0; i < 60; i++) {
      ctx.beginPath();
      const sx = Math.random() * px;
      const sy = Math.random() * px;
      ctx.moveTo(sx, sy);
      for (let s = 0; s < 5; s++) {
        ctx.lineTo(sx + (Math.random() - 0.5) * 80, sy + (Math.random() - 0.5) * 80);
      }
      ctx.stroke();
    }
    // Parking-lot-style area (loading bay)
    paintParkingLot(ctx, px * 0.06, px * 0.08, px * 0.30, px * 0.18);
  },

  // Downtown — denser asphalt grid + parking lots + sidewalks
  downtown(ctx, px) {
    paintGrassBase(ctx, px, "#1f2230");
    const roadW = px * 0.12;
    const sw = px * 0.02;
    // Two horizontal + two vertical roads
    const rowsY = [px * 0.28, px * 0.72];
    const colsX = [px * 0.28, px * 0.72];
    for (const y of rowsY) {
      const hY = y - roadW / 2;
      paintSidewalkStrip(ctx, 0, hY - sw, px, sw);
      paintSidewalkStrip(ctx, 0, hY + roadW, px, sw);
      paintRoad(ctx, 0, hY, px, roadW, true);
    }
    for (const x of colsX) {
      const vX = x - roadW / 2;
      paintSidewalkStrip(ctx, vX - sw, 0, sw, px);
      paintSidewalkStrip(ctx, vX + roadW, 0, sw, px);
      paintRoad(ctx, vX, 0, roadW, px, false);
    }
    // Crosswalks at intersections
    for (const y of rowsY) {
      for (const x of colsX) {
        paintCrosswalk(ctx, x - roadW / 2 - sw / 2, y, roadW, sw - 4, false);
        paintCrosswalk(ctx, x + roadW / 2 + sw / 2, y, roadW, sw - 4, false);
        paintCrosswalk(ctx, x, y - roadW / 2 - sw / 2, roadW, sw - 4, true);
        paintCrosswalk(ctx, x, y + roadW / 2 + sw / 2, roadW, sw - 4, true);
      }
    }
    // A couple of parking lots in the city blocks
    paintParkingLot(ctx, px * 0.03, px * 0.03, px * 0.20, px * 0.18);
    paintParkingLot(ctx, px * 0.78, px * 0.78, px * 0.18, px * 0.18);
  },
};

// Generic painter for any new theme — solid ground tint + organic noise so
// it doesn't look flat. Used by all Phase 6 worlds (carnival/moonbase/
// swamp/neoncity/graveyard/alienlab) which don't need painted roads/paths.
function paintTintBase(ctx, px, baseColor) {
  ctx.fillStyle = baseColor;
  ctx.fillRect(0, 0, px, px);
  // Soft noise blotches
  for (let i = 0; i < 800; i++) {
    const x = Math.random() * px;
    const y = Math.random() * px;
    const r = 4 + Math.random() * 18;
    ctx.fillStyle = `rgba(0,0,0,${0.04 + Math.random() * 0.06})`;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  for (let i = 0; i < 600; i++) {
    const x = Math.random() * px;
    const y = Math.random() * px;
    const r = 3 + Math.random() * 12;
    ctx.fillStyle = `rgba(255,255,255,${0.03 + Math.random() * 0.05})`;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

// Procedural ground texture — generated once per level theme. Adds subtle
// noise patches, soft darker blotches and a faint grid so the floor reads as
// terrain instead of a flat colour. Cheap: one 512x512 canvas, tiled.
function makeGroundTexture(level, textureSize = 1024) {
  const size = textureSize;
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const ctx = c.getContext("2d");
  const painter = THEME_PAINTERS[level.theme];
  if (painter) {
    painter(ctx, size, level);
  } else {
    // Generic tint painter for Phase 6 worlds — uses the level's groundColor.
    paintTintBase(ctx, size, level.groundColor || "#1f3d1f");
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.repeat.set(1, 1);
  tex.anisotropy = 4;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

// A glowing neon perimeter — base rail, evenly-spaced posts with bulb caps,
// and a translucent emissive sheet acting as a "force field".
function EnergyWall({ position, length, rotation = 0, color, height = 2.4 }) {
  const postCount = Math.max(2, Math.floor(length / 7) + 1);
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      {/* base rail */}
      <mesh position={[0, 0.15, 0]} castShadow>
        <boxGeometry args={[length, 0.3, 0.45]} />
        <meshStandardMaterial color="#1c1530" metalness={0.65} roughness={0.4} />
      </mesh>
      {/* energy field plane */}
      <mesh position={[0, height / 2 + 0.15, 0]}>
        <planeGeometry args={[length, height]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={0.7}
          transparent
          opacity={0.18}
          side={THREE.DoubleSide}
          depthWrite={false}
          toneMapped={false}
        />
      </mesh>
      {/* top rail (thin glowing line) */}
      <mesh position={[0, height + 0.15, 0]}>
        <boxGeometry args={[length, 0.06, 0.06]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={1.8}
          toneMapped={false}
        />
      </mesh>
      {/* posts */}
      {Array.from({ length: postCount }).map((_, i) => {
        const t = postCount === 1 ? 0.5 : i / (postCount - 1);
        const x = -length / 2 + t * length;
        return (
          <group key={i} position={[x, 0, 0]}>
            <mesh position={[0, height / 2 + 0.15, 0]} castShadow>
              <cylinderGeometry args={[0.09, 0.12, height, 8]} />
              <meshStandardMaterial color="#2a2238" metalness={0.7} roughness={0.4} />
            </mesh>
            <mesh position={[0, height + 0.32, 0]}>
              <sphereGeometry args={[0.16, 12, 12]} />
              <meshStandardMaterial
                color={color}
                emissive={color}
                emissiveIntensity={2}
                toneMapped={false}
              />
            </mesh>
          </group>
        );
      })}
    </group>
  );
}

// A corner beacon — taller pillar topped with a glowing orb + cheap point
// light. Adds depth/silhouette to the four corners of the play area.
function CornerBeacon({ position, color }) {
  return (
    <group position={position}>
      <mesh position={[0, 1.4, 0]} castShadow>
        <cylinderGeometry args={[0.25, 0.45, 2.8, 10]} />
        <meshStandardMaterial color="#2a2238" metalness={0.75} roughness={0.35} />
      </mesh>
      <mesh position={[0, 3.0, 0]}>
        <sphereGeometry args={[0.35, 16, 16]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={2.4}
          toneMapped={false}
        />
      </mesh>
      <pointLight position={[0, 3.0, 0]} intensity={0.7} color={color} distance={14} />
    </group>
  );
}

// Tree - low-poly cone + cylinder
function Tree({ position, scale = 1 }) {
  return (
    <group position={position} scale={[scale, scale, scale]}>
      <mesh position={[0, 0.6, 0]} castShadow>
        <cylinderGeometry args={[0.18, 0.22, 1.2, 6]} />
        <meshStandardMaterial color="#4a2a18" roughness={0.95} />
      </mesh>
      <mesh position={[0, 2.0, 0]} castShadow>
        <coneGeometry args={[1.1, 2.0, 8]} />
        <meshStandardMaterial color="#2d5a2d" roughness={0.85} />
      </mesh>
      <mesh position={[0, 2.9, 0]} castShadow>
        <coneGeometry args={[0.8, 1.4, 8]} />
        <meshStandardMaterial color="#3a6b3a" roughness={0.85} />
      </mesh>
    </group>
  );
}

function Fence({ position, rotation = 0, length = 4 }) {
  const posts = Math.floor(length / 0.6);
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      <mesh position={[0, 0.5, 0]} castShadow>
        <boxGeometry args={[length, 0.08, 0.08]} />
        <meshStandardMaterial color="#6b4a2a" roughness={0.9} />
      </mesh>
      <mesh position={[0, 0.9, 0]} castShadow>
        <boxGeometry args={[length, 0.08, 0.08]} />
        <meshStandardMaterial color="#6b4a2a" roughness={0.9} />
      </mesh>
      {Array.from({ length: posts }).map((_, i) => (
        <mesh
          key={i}
          position={[-length / 2 + i * (length / (posts - 1 || 1)), 0.55, 0]}
          castShadow
        >
          <boxGeometry args={[0.1, 1.2, 0.1]} />
          <meshStandardMaterial color="#5a3a22" roughness={0.95} />
        </mesh>
      ))}
    </group>
  );
}

function Shed({ position, rotation = 0 }) {
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      <mesh position={[0, 0.9, 0]} castShadow>
        <boxGeometry args={[2.5, 1.8, 2]} />
        <meshStandardMaterial color="#7a4a2a" roughness={0.85} />
      </mesh>
      <mesh position={[0, 2.0, 0]} castShadow>
        <coneGeometry args={[1.9, 0.7, 4]} rotation={[0, Math.PI / 4, 0]} />
        <meshStandardMaterial color="#3a2018" roughness={0.9} />
      </mesh>
    </group>
  );
}

function House({ position, rotation = 0, color = "#8a5a3a" }) {
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      <mesh position={[0, 1.2, 0]} castShadow>
        <boxGeometry args={[4, 2.4, 3.2]} />
        <meshStandardMaterial color={color} roughness={0.85} />
      </mesh>
      <mesh position={[0, 2.8, 0]} rotation={[0, Math.PI / 4, 0]} castShadow>
        <coneGeometry args={[2.8, 1.4, 4]} />
        <meshStandardMaterial color="#3a1a1a" roughness={0.9} />
      </mesh>
      {/* Door */}
      <mesh position={[0, 0.7, 1.61]}>
        <boxGeometry args={[0.6, 1.2, 0.05]} />
        <meshStandardMaterial color="#4a2a1a" />
      </mesh>
      {/* Window glow */}
      <mesh position={[-1.2, 1.4, 1.61]}>
        <boxGeometry args={[0.7, 0.7, 0.05]} />
        <meshStandardMaterial
          color="#ffd86b"
          emissive="#ffd86b"
          emissiveIntensity={0.8}
          toneMapped={false}
        />
      </mesh>
      <mesh position={[1.2, 1.4, 1.61]}>
        <boxGeometry args={[0.7, 0.7, 0.05]} />
        <meshStandardMaterial
          color="#ffd86b"
          emissive="#ffd86b"
          emissiveIntensity={0.8}
          toneMapped={false}
        />
      </mesh>
    </group>
  );
}

function Car({ position, rotation = 0, color = "#b04a3a" }) {
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      <mesh position={[0, 0.45, 0]} castShadow>
        <boxGeometry args={[2.2, 0.6, 1.1]} />
        <meshStandardMaterial color={color} metalness={0.6} roughness={0.35} />
      </mesh>
      <mesh position={[-0.1, 1.0, 0]} castShadow>
        <boxGeometry args={[1.3, 0.55, 1.0]} />
        <meshStandardMaterial color={color} metalness={0.6} roughness={0.35} />
      </mesh>
      {/* Wheels */}
      {[[-0.7, -0.5], [0.7, -0.5], [-0.7, 0.5], [0.7, 0.5]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.25, z]} rotation={[0, 0, Math.PI / 2]} castShadow>
          <cylinderGeometry args={[0.25, 0.25, 0.18, 12]} />
          <meshStandardMaterial color="#1a1a1a" roughness={0.95} />
        </mesh>
      ))}
    </group>
  );
}

function Mailbox({ position }) {
  return (
    <group position={position}>
      <mesh position={[0, 0.5, 0]} castShadow>
        <cylinderGeometry args={[0.05, 0.05, 1, 6]} />
        <meshStandardMaterial color="#3a2a1a" />
      </mesh>
      <mesh position={[0, 1.1, 0]} castShadow>
        <boxGeometry args={[0.4, 0.3, 0.5]} />
        <meshStandardMaterial color="#b04a3a" metalness={0.4} roughness={0.5} />
      </mesh>
    </group>
  );
}

function HayBale({ position, rotation = 0 }) {
  return (
    <mesh position={position} rotation={[Math.PI / 2, 0, rotation]} castShadow>
      <cylinderGeometry args={[0.7, 0.7, 1.2, 12]} />
      <meshStandardMaterial color="#c89858" roughness={0.95} />
    </mesh>
  );
}

function Barn({ position, rotation = 0 }) {
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      <mesh position={[0, 1.6, 0]} castShadow>
        <boxGeometry args={[5, 3.2, 4]} />
        <meshStandardMaterial color="#9a2a2a" roughness={0.85} />
      </mesh>
      <mesh position={[0, 3.7, 0]} rotation={[0, Math.PI / 4, 0]} castShadow>
        <coneGeometry args={[3.4, 1.6, 4]} />
        <meshStandardMaterial color="#3a1a1a" roughness={0.9} />
      </mesh>
    </group>
  );
}

function JunkPile({ position }) {
  return (
    <group position={position}>
      <mesh position={[0, 0.4, 0]} castShadow>
        <boxGeometry args={[1.4, 0.8, 1.2]} />
        <meshStandardMaterial color="#5a4a3a" roughness={0.95} />
      </mesh>
      <mesh position={[0.3, 0.95, 0.2]} rotation={[0, 0.3, 0.2]} castShadow>
        <boxGeometry args={[0.8, 0.5, 0.8]} />
        <meshStandardMaterial color="#3a3328" roughness={0.95} />
      </mesh>
      <mesh position={[-0.2, 1.2, -0.1]} rotation={[0.1, -0.4, 0.1]} castShadow>
        <boxGeometry args={[0.5, 0.4, 0.6]} />
        <meshStandardMaterial color="#6a5a4a" metalness={0.4} roughness={0.7} />
      </mesh>
    </group>
  );
}

function Tire({ position }) {
  return (
    <mesh position={position} rotation={[Math.PI / 2, 0, 0]} castShadow>
      <torusGeometry args={[0.4, 0.18, 8, 16]} />
      <meshStandardMaterial color="#15151a" roughness={0.95} />
    </mesh>
  );
}

function Building({ position, height = 6, color = "#3a3f55" }) {
  return (
    <group position={position}>
      <mesh position={[0, height / 2, 0]} castShadow>
        <boxGeometry args={[3.5, height, 3.5]} />
        <meshStandardMaterial color={color} roughness={0.8} />
      </mesh>
      {/* Lit windows */}
      {Array.from({ length: Math.floor(height) }).map((_, row) =>
        [-1, 0, 1].map((col, i) => (
          <mesh key={`${row}-${i}`} position={[col, row + 0.8, 1.76]}>
            <boxGeometry args={[0.4, 0.4, 0.02]} />
            <meshStandardMaterial
              color={Math.random() > 0.4 ? "#ffd86b" : "#1a1a2a"}
              emissive={Math.random() > 0.4 ? "#ffd86b" : "#000"}
              emissiveIntensity={0.7}
              toneMapped={false}
            />
          </mesh>
        ))
      )}
    </group>
  );
}

function StreetLight({ position }) {
  return (
    <group position={position}>
      <mesh position={[0, 1.5, 0]} castShadow>
        <cylinderGeometry args={[0.06, 0.08, 3, 6]} />
        <meshStandardMaterial color="#2a2a32" metalness={0.7} roughness={0.4} />
      </mesh>
      <mesh position={[0, 3.05, 0]}>
        <sphereGeometry args={[0.25, 12, 12]} />
        <meshStandardMaterial
          color="#fff2b8"
          emissive="#fff2b8"
          emissiveIntensity={1.5}
          toneMapped={false}
        />
      </mesh>
      <pointLight position={[0, 3, 0]} intensity={0.6} color="#fff2b8" distance={6} />
    </group>
  );
}

// ===== Phase 6 new props (Carnival / Moon / Swamp / Neon / Graveyard / Alien) =====

function Tombstone({ position, rotation = 0 }) {
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      <mesh position={[0, 0.6, 0]} castShadow>
        <boxGeometry args={[0.9, 1.2, 0.25]} />
        <meshStandardMaterial color="#6a6a78" roughness={0.95} />
      </mesh>
      <mesh position={[0, 1.3, 0]} castShadow>
        <cylinderGeometry args={[0.45, 0.45, 0.25, 12, 1, false, 0, Math.PI]} />
        <meshStandardMaterial color="#6a6a78" roughness={0.95} />
      </mesh>
      <mesh position={[0, 0, 0]}>
        <boxGeometry args={[1.1, 0.05, 0.45]} />
        <meshStandardMaterial color="#3a3a48" roughness={1} />
      </mesh>
    </group>
  );
}

function MoonRock({ position, rotation = 0 }) {
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      <mesh position={[0, 0.4, 0]} castShadow>
        <dodecahedronGeometry args={[0.9, 0]} />
        <meshStandardMaterial color="#9a9aa8" roughness={0.95} />
      </mesh>
      <mesh position={[0.5, 0.2, 0.3]} castShadow>
        <dodecahedronGeometry args={[0.4, 0]} />
        <meshStandardMaterial color="#7a7a88" roughness={0.95} />
      </mesh>
    </group>
  );
}

function Crater({ position }) {
  return (
    <group position={position}>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, 0]}>
        <ringGeometry args={[1.2, 1.8, 24]} />
        <meshStandardMaterial color="#2a2a30" roughness={1} side={2} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
        <circleGeometry args={[1.2, 24]} />
        <meshStandardMaterial color="#1a1a22" roughness={1} />
      </mesh>
    </group>
  );
}

function NeonSign({ position, rotation = 0 }) {
  const color = ["#ff2bdc", "#5fffcf", "#ffd06b", "#84e6ff"][Math.floor(position[0] * 7 + 11) % 4];
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      <mesh position={[0, 1.8, 0]} castShadow>
        <boxGeometry args={[0.18, 3.6, 0.18]} />
        <meshStandardMaterial color="#1a1a22" metalness={0.6} roughness={0.5} />
      </mesh>
      <mesh position={[0.9, 2.6, 0]} castShadow>
        <boxGeometry args={[1.8, 0.8, 0.12]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={1.4}
          toneMapped={false}
        />
      </mesh>
      {/* Emissive material handles the glow — no extra pointLight (saves
          GPU on Neon City which already has 10+ neon signs). */}
    </group>
  );
}

function CarnivalTent({ position, rotation = 0 }) {
  const stripes = ["#ff5fb4", "#fff"];
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      {/* Tent body — 6 alternating stripes */}
      {Array.from({ length: 6 }).map((_, i) => (
        <mesh
          key={i}
          position={[0, 1.2, 0]}
          rotation={[0, (i * Math.PI) / 3, 0]}
          castShadow
        >
          <coneGeometry args={[2.2, 2.4, 3, 1, false, 0, Math.PI / 3]} />
          <meshStandardMaterial color={stripes[i % 2]} roughness={0.7} />
        </mesh>
      ))}
      {/* Spike on top */}
      <mesh position={[0, 2.7, 0]}>
        <cylinderGeometry args={[0.03, 0.03, 0.5, 6]} />
        <meshStandardMaterial color="#ffd06b" />
      </mesh>
      <mesh position={[0, 3.0, 0]}>
        <sphereGeometry args={[0.18, 10, 10]} />
        <meshStandardMaterial color="#ffd06b" emissive="#ffd06b" emissiveIntensity={0.6} toneMapped={false} />
      </mesh>
    </group>
  );
}

function Booth({ position, rotation = 0 }) {
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      {/* Counter */}
      <mesh position={[0, 0.6, 0]} castShadow>
        <boxGeometry args={[2.4, 1.2, 1.2]} />
        <meshStandardMaterial color="#9a3a6a" roughness={0.8} />
      </mesh>
      {/* Striped awning */}
      <mesh position={[0, 1.6, 0.4]} rotation={[-0.2, 0, 0]} castShadow>
        <boxGeometry args={[2.6, 0.05, 1.6]} />
        <meshStandardMaterial color="#fff" roughness={0.7} />
      </mesh>
      {/* Posts */}
      <mesh position={[-1.1, 1.3, 0.6]}>
        <cylinderGeometry args={[0.06, 0.06, 1.4, 6]} />
        <meshStandardMaterial color="#5a2540" />
      </mesh>
      <mesh position={[1.1, 1.3, 0.6]}>
        <cylinderGeometry args={[0.06, 0.06, 1.4, 6]} />
        <meshStandardMaterial color="#5a2540" />
      </mesh>
    </group>
  );
}

function AlienTube({ position, rotation = 0 }) {
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      <mesh position={[0, 0.1, 0]}>
        <cylinderGeometry args={[0.7, 0.7, 0.2, 16]} />
        <meshStandardMaterial color="#222a3a" metalness={0.5} roughness={0.4} />
      </mesh>
      <mesh position={[0, 1.4, 0]}>
        <cylinderGeometry args={[0.55, 0.55, 2.4, 16, 1, true]} />
        <meshStandardMaterial
          color="#5fffcf"
          emissive="#5fffcf"
          emissiveIntensity={0.9}
          toneMapped={false}
          transparent
          opacity={0.55}
          side={2}
        />
      </mesh>
      <mesh position={[0, 2.7, 0]}>
        <cylinderGeometry args={[0.7, 0.7, 0.2, 16]} />
        <meshStandardMaterial color="#222a3a" metalness={0.6} roughness={0.35} />
      </mesh>
      {/* Emissive material lights the tube — no extra pointLight needed. */}
    </group>
  );
}

function ToxicPuddle({ position }) {
  return (
    <group position={position}>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
        <circleGeometry args={[1.6, 24]} />
        <meshStandardMaterial
          color="#6fff7a"
          emissive="#6fff7a"
          emissiveIntensity={0.5}
          toneMapped={false}
          roughness={0.2}
          metalness={0.1}
        />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0.4, 0.03, 0.2]}>
        <circleGeometry args={[0.25, 16]} />
        <meshStandardMaterial color="#a8ffa8" emissive="#a8ffa8" emissiveIntensity={0.8} toneMapped={false} />
      </mesh>
      {/* Emissive surface only — no pointLight (Toxic Swamp has 10 of these). */}
    </group>
  );
}

function Cornstalk({ position, rotation = 0 }) {
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      <mesh position={[0, 1.0, 0]}>
        <cylinderGeometry args={[0.04, 0.06, 2, 4]} />
        <meshStandardMaterial color="#6a6020" roughness={0.95} />
      </mesh>
      {/* Leaves */}
      {[0, 1, 2].map((i) => (
        <mesh
          key={i}
          position={[0, 1.0 + i * 0.4, 0]}
          rotation={[0, (i * 2.1), 0.4]}
        >
          <coneGeometry args={[0.4, 0.7, 4]} />
          <meshStandardMaterial color="#7a8a3a" roughness={0.9} />
        </mesh>
      ))}
      {/* Cob */}
      <mesh position={[0.15, 1.5, 0]}>
        <cylinderGeometry args={[0.07, 0.07, 0.35, 6]} />
        <meshStandardMaterial color="#ffd06b" roughness={0.7} />
      </mesh>
    </group>
  );
}

// ===== /Phase 6 new props =====

// Helper to check if obstacle overlaps an existing one
function tooClose(x, z, used, minDist = 3) {
  for (const p of used) {
    const dx = p[0] - x;
    const dz = p[1] - z;
    if (dx * dx + dz * dz < minDist * minDist) return true;
  }
  return false;
}

// Returns true when the world (x, z) lands on a paved region of the level
// texture (road, sidewalk, parking lot, stone path). Used to keep buildings,
// trees, fences, mailboxes etc. on the grassy/dirt areas. Cars and
// streetlights are exempt from this filter.
function isOnPaved(level, x, z) {
  const size = level.mapSize;
  const nx = x / size; // -0.5..0.5
  const nz = z / size;
  const m = 1.6 / size; // ~1.6 world-unit margin so things don't hug the curb

  switch (level.theme) {
    case "backyard":
      // Stone path vertical: canvas u [0.38, 0.44] → world X [-0.12, -0.06]
      if (nx > -0.13 - m && nx < -0.05 + m) return true;
      // Stone path horizontal: canvas v [0.55, 0.60] → world Z [0.05, 0.10]
      if (nz > 0.04 - m && nz < 0.11 + m) return true;
      return false;
    case "street": {
      // Centre cross of road + sidewalks: total half-width ≈ 0.094 (norm).
      const cross = 0.094 + m;
      if (Math.abs(nz) < cross) return true;
      if (Math.abs(nx) < cross) return true;
      // Parking lot 1 (top-right)
      if (nx > 0.28 - m && nx < 0.46 + m && nz > -0.40 - m && nz < -0.30 + m) return true;
      // Parking lot 2 (bottom-left)
      if (nx > -0.44 - m && nx < -0.28 + m && nz > 0.28 - m && nz < 0.40 + m) return true;
      // Driveway connectors
      if (nx > 0.36 - m && nx < 0.40 + m && nz > -0.30 - m && nz < -0.07 + m) return true;
      if (nx > -0.38 - m && nx < -0.34 + m && nz > 0.24 - m && nz < 0.28 + m) return true;
      return false;
    }
    case "farm":
      // Dirt path: canvas v [0.52, 0.57] → Z [0.02, 0.07]
      if (nz > 0.01 - m && nz < 0.08 + m) return true;
      return false;
    case "junkyard":
      // Loading bay
      if (nx > -0.44 - m && nx < -0.14 + m && nz > -0.42 - m && nz < -0.24 + m) return true;
      return false;
    case "downtown": {
      // Two horizontal + two vertical roads, half-band including sidewalks
      const halfBand = 0.08 + m;
      if (Math.abs(nz + 0.22) < halfBand) return true;
      if (Math.abs(nz - 0.22) < halfBand) return true;
      if (Math.abs(nx + 0.22) < halfBand) return true;
      if (Math.abs(nx - 0.22) < halfBand) return true;
      // Corner parking lots
      if (nx > -0.47 - m && nx < -0.27 + m && nz > -0.47 - m && nz < -0.29 + m) return true;
      if (nx > 0.27 - m && nx < 0.47 + m && nz > 0.27 - m && nz < 0.47 + m) return true;
      return false;
    }
    default:
      return false;
  }
}

// Obstacles allowed on paved tiles (cars belong on streets, streetlights on
// sidewalks). Everything else is filtered onto grass/dirt.
const PAVED_OK = new Set(["car", "streetlight"]);

// Generate obstacles based on world's `props` plan (data-driven). Each
// World defines `props: [{ maker, count, minDist }]` — this just iterates
// and places them while respecting the paved-area filter.
export function generateObstacles(level) {
  const rng = mulberry32(level.id * 9973);
  const half = level.mapSize / 2 - 4;
  const items = [];
  const used = [];

  function place(maker, count, minDist = 3) {
    let tries = 0;
    while (items.filter((i) => i.maker === maker).length < count && tries < count * 50) {
      tries++;
      const x = (rng() * 2 - 1) * half;
      const z = (rng() * 2 - 1) * half;
      if (Math.abs(x) < 6 && Math.abs(z) < 6) continue;
      if (!PAVED_OK.has(maker) && isOnPaved(level, x, z)) continue;
      if (tooClose(x, z, used, minDist)) continue;
      const rot = rng() * Math.PI * 2;
      items.push({ maker, x, z, rot });
      used.push([x, z]);
    }
  }

  // Data-driven: each world declares its own props plan in worlds.js.
  const plan = Array.isArray(level.props) ? level.props : [];
  for (const p of plan) {
    place(p.maker, p.count, p.minDist || 3);
  }
  return items;
}

export function isObstacleAt(obstacles, x, z, radius = 1.5) {
  for (const o of obstacles) {
    const dx = o.x - x;
    const dz = o.z - z;
    const r = radius + (o.size || 2);
    if (dx * dx + dz * dz < r * r) return true;
  }
  return false;
}

function MapScene({ level, obstacles, quality }) {
  const size = level.mapSize;
  const half = size / 2;
  const texSize = quality?.groundTextureSize || 1024;
  const lowQ = quality?.effective === "low";

  // Procedural ground texture is memoized per-level + texture-size.
  const groundTexture = useMemo(
    () => makeGroundTexture(level, texSize),
    [level, texSize]
  );
  // Per-level accent colour for the perimeter glow.
  const neon = level.accentColor;

  const items = useMemo(() => {
    return obstacles.map((it, idx) => {
      const pos = [it.x, 0, it.z];
      switch (it.maker) {
        case "tree":
          return <Tree key={idx} position={pos} scale={0.9 + (idx % 3) * 0.2} />;
        case "fence":
          return <Fence key={idx} position={pos} rotation={it.rot} length={4} />;
        case "shed":
          return <Shed key={idx} position={pos} rotation={it.rot} />;
        case "house":
          return <House key={idx} position={pos} rotation={it.rot} color={idx % 2 ? "#8a5a3a" : "#6a4a8a"} />;
        case "car":
          return <Car key={idx} position={pos} rotation={it.rot} color={["#b04a3a", "#3a6a9a", "#9a8a3a"][idx % 3]} />;
        case "mailbox":
          return <Mailbox key={idx} position={pos} />;
        case "barn":
          return <Barn key={idx} position={pos} rotation={it.rot} />;
        case "haybale":
          return <HayBale key={idx} position={pos} rotation={it.rot} />;
        case "junk":
          return <JunkPile key={idx} position={pos} />;
        case "tire":
          return <Tire key={idx} position={pos} />;
        case "building":
          return <Building key={idx} position={pos} height={5 + (idx % 4) * 1.5} color={["#3a3f55", "#4a3a55", "#3a4a55"][idx % 3]} />;
        case "streetlight":
          return <StreetLight key={idx} position={pos} />;
        case "tombstone":
          return <Tombstone key={idx} position={pos} rotation={it.rot} />;
        case "moonrock":
          return <MoonRock key={idx} position={pos} rotation={it.rot} />;
        case "crater":
          return <Crater key={idx} position={pos} />;
        case "neonsign":
          return <NeonSign key={idx} position={pos} rotation={it.rot} />;
        case "tent":
          return <CarnivalTent key={idx} position={pos} rotation={it.rot} />;
        case "booth":
          return <Booth key={idx} position={pos} rotation={it.rot} />;
        case "alientube":
          return <AlienTube key={idx} position={pos} rotation={it.rot} />;
        case "toxicpuddle":
          return <ToxicPuddle key={idx} position={pos} />;
        case "cornstalk":
          return <Cornstalk key={idx} position={pos} rotation={it.rot} />;
        default:
          return null;
      }
    });
  }, [obstacles]);

  return (
    <group>
      {/* Main ground — textured for depth */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow={!lowQ}>
        <planeGeometry args={[size, size, 1, 1]} />
        <meshStandardMaterial map={groundTexture} roughness={0.95} metalness={0} />
      </mesh>

      {/* Soft edge vignette — slight dim around the perimeter only */}
      <mesh position={[0, 0.005, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[half * 0.78, half + 1, 64]} />
        <meshBasicMaterial color="#000000" transparent opacity={0.22} depthWrite={false} />
      </mesh>

      {/* Centre highlight — subtle warm pool under spawn */}
      <mesh position={[0, 0.006, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[6, 32]} />
        <meshBasicMaterial color={neon} transparent opacity={0.08} depthWrite={false} />
      </mesh>

      {/* Neon energy perimeter */}
      <EnergyWall position={[0, 0, -half]} length={size} rotation={0} color={neon} />
      <EnergyWall position={[0, 0, half]} length={size} rotation={Math.PI} color={neon} />
      <EnergyWall position={[-half, 0, 0]} length={size} rotation={-Math.PI / 2} color={neon} />
      <EnergyWall position={[half, 0, 0]} length={size} rotation={Math.PI / 2} color={neon} />

      {/* Corner beacons */}
      <CornerBeacon position={[-half, 0, -half]} color={neon} />
      <CornerBeacon position={[half, 0, -half]} color={neon} />
      <CornerBeacon position={[-half, 0, half]} color={neon} />
      <CornerBeacon position={[half, 0, half]} color={neon} />

      {/* Safe zone marker (kept) */}
      <mesh position={[0, 0.05, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[3, 3.4, 32]} />
        <meshStandardMaterial
          color="#7ed957"
          emissive="#7ed957"
          emissiveIntensity={0.8}
          toneMapped={false}
        />
      </mesh>
      <mesh position={[0, 0.04, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[2.6, 2.8, 32]} />
        <meshStandardMaterial
          color="#7ed957"
          emissive="#7ed957"
          emissiveIntensity={0.4}
          transparent
          opacity={0.5}
          toneMapped={false}
        />
      </mesh>

      {items}
    </group>
  );
}

export default React.memo(MapScene);
